import React, { useState, useEffect, useCallback } from 'react';
import { getCheckoutOptions, formatCheckout } from './utils/checkouts';
import './CheckoutTraining.css';

// Imágenes de patrocinadores
import patrocinio1 from '../../assets/patrocinio1.jpg';
import patrocinio2 from '../../assets/patrocinio2.jpg';
import triple17 from '../../assets/triple17.png';
import triple17yeyo from '../../assets/Triple17yeyo2.png';
import logoDani from '../../assets/logoDani.png';
import logoPrima from '../../assets/LogoPrima.png';
import Cad from '../../assets/Cad.jpg';
import Lokyo from '../../assets/Lokyo.jpg';
import Cocktails from '../../assets/Cocktails.jpg';
import KingDart from '../../assets/KingDart.jpg';
import LogoRocha from '../../assets/LogoRocha.png';
import CollaboratorsSection from './CollaboratorsSection';

// ========== GENERADORES DE CIERRES POR DIFICULTAD ==========

const DOUBLES_BY_DIFFICULTY = {
  1: [40, 32, 20], // D20, D16, D10 - Los más comunes y cómodos
  2: [36, 16, 24], // D18, D8, D12 - Frecuentes en partidas
  3: [28, 12, 8, 4], // D14, D6, D4, D2 - Menos practicados
  4: [38, 34, 30, 26, 22, 18, 14, 10, 6, 2], // Dobles impares y pequeños
  5: [50], // Bull - El más difícil
};

const TWO_DART_BY_DIFFICULTY = {
  1: [61, 64, 66, 68, 70], // T + D20 directos y cómodos
  2: [72, 74, 76, 78, 80], // T + D clásicos
  3: [81, 82, 84, 85, 87, 88], // Empiezan a variar más
  4: [89, 90, 91, 92, 94, 95, 96], // Combinaciones menos intuitivas
  5: [97, 98, 100], // Los tops de 2 dardos
};

const THREE_DART_BY_DIFFICULTY = {
  1: [101, 102, 104, 105, 106], // T20 + S + D20 directos
  2: [107, 108, 110, 111, 112], // Similares, muy accesibles
  3: [114, 116, 118, 120, 121, 124], // Empiezan las variaciones
  4: [127, 130, 132, 135, 138, 140, 141, 145], // Dobles triples consecutivos
  5: [158, 160, 161, 164, 167, 170], // Los "big fish" con Bull
};

const generateCheckout = (level, difficulty) => {
  let pool;
  let dartsNeeded;
  
  if (level === 1) {
    pool = DOUBLES_BY_DIFFICULTY[difficulty] || DOUBLES_BY_DIFFICULTY[1];
    dartsNeeded = 1;
  } else if (level === 2) {
    pool = TWO_DART_BY_DIFFICULTY[difficulty] || TWO_DART_BY_DIFFICULTY[1];
    dartsNeeded = 2;
  } else {
    pool = THREE_DART_BY_DIFFICULTY[difficulty] || THREE_DART_BY_DIFFICULTY[1];
    dartsNeeded = 3;
  }
  
  const target = pool[Math.floor(Math.random() * pool.length)];
  const options = getCheckoutOptions(target);
  
  return {
    target,
    solution: level === 1 ? (target === 50 ? 'BULL' : `D${target / 2}`) : (options[0] || ''),
    dartsNeeded,
    difficulty
  };
};

const generateLevelCheckouts = (level) => {
  return [1, 2, 3, 4, 5].map(diff => generateCheckout(level, diff));
};

// ========== MODAL DE RESULTADOS ==========

const ResultModal = ({ visible, type, currentLevel, levelRetries, allRetries, onContinue, onExit }) => {
  if (!visible) return null;

  const isComplete = type === 'training-complete';
  const totalRetries = allRetries.reduce((a, b) => a + b, 0);

  return (
    <div className="modal-overlay">
      <div className="modal-content result-modal">
        <div className={`modal-icon ${isComplete ? 'complete' : 'level'}`}>
          {isComplete ? '🏆' : '⭐'}
        </div>
        
        <h2 className="modal-title">
          {isComplete ? '¡Entrenamiento Completado!' : `¡Nivel ${currentLevel} Completado!`}
        </h2>
        
        {isComplete ? (
          <div className="modal-stats">
            <p>Has completado los 3 niveles de entrenamiento</p>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Nivel 1</span>
                <span className="stat-value">{allRetries[0]} reintentos</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Nivel 2</span>
                <span className="stat-value">{allRetries[1]} reintentos</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Nivel 3</span>
                <span className="stat-value">{allRetries[2]} reintentos</span>
              </div>
              <div className="stat-item total">
                <span className="stat-label">Total</span>
                <span className="stat-value">{totalRetries} reintentos</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="modal-stats">
            <p>Reintentos en este nivel: <strong>{levelRetries}</strong></p>
            <p className="next-level-text">Siguiente: Nivel {currentLevel + 1}</p>
          </div>
        )}
        
        <div className="modal-buttons">
          <button className="btn-secondary" onClick={onExit}>
            Salir
          </button>
          <button className="btn-primary" onClick={onContinue}>
            {isComplete ? 'Reiniciar' : 'Continuar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ========== COMPONENTE PRINCIPAL ==========

const CheckoutTraining = ({ onBack }) => {
  // Estado del juego
  const [currentLevel, setCurrentLevel] = useState(1);
  const [currentCheckoutIndex, setCurrentCheckoutIndex] = useState(0);
  const [checkouts, setCheckouts] = useState([]);
  
  // Reintentos por nivel
  const [retries, setRetries] = useState([0, 0, 0]);
  
  // Fallos consecutivos en el checkout actual (0 = sin fallos, 1 = ya falló 1 vez)
  const [currentFailCount, setCurrentFailCount] = useState(0);
  
  // Estado de modales
  const [resultModal, setResultModal] = useState({
    visible: false,
    type: 'level-complete',
  });
  
  // Estado de confirmación para salir
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const currentCheckout = checkouts[currentCheckoutIndex];

  // Inicializar al montar
  useEffect(() => {
    setCheckouts(generateLevelCheckouts(1));
  }, []);

  // Manejar teclas del teclado
  const handleKeyPress = useCallback((e) => {
    if (resultModal.visible || showExitConfirm) return;
    
    if (e.key === 'Enter' || e.key === ' ' || e.key === 's' || e.key === 'S') {
      e.preventDefault();
      handleSuccess();
    } else if (e.key === 'Escape' || e.key === 'n' || e.key === 'N') {
      e.preventDefault();
      if (e.key === 'Escape') {
        setShowExitConfirm(true);
      } else {
        handleFail();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultModal.visible, showExitConfirm, currentCheckout]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  // ========== LÓGICA DEL JUEGO ==========

  const handleSuccess = () => {
    if (!currentCheckout) return;
    
    // Resetear contador de fallos al acertar
    setCurrentFailCount(0);
    
    const isLastCheckout = currentCheckoutIndex === 4;
    const isLastLevel = currentLevel === 3;
    
    if (isLastCheckout && isLastLevel) {
      // Mostrar modal de entrenamiento completado
      setResultModal({ visible: true, type: 'training-complete' });
    } else if (isLastCheckout) {
      // Mostrar modal de nivel completado
      setResultModal({ visible: true, type: 'level-complete' });
    } else {
      // Siguiente cierre
      setCurrentCheckoutIndex(prev => prev + 1);
    }
  };

  const handleFail = () => {
    if (!currentCheckout) return;
    
    // Incrementar reintentos del nivel actual
    const newRetries = [...retries];
    newRetries[currentLevel - 1]++;
    setRetries(newRetries);
    
    if (currentFailCount === 0) {
      // Primer fallo: repite el MISMO número (segunda oportunidad)
      setCurrentFailCount(1);
    } else {
      // Segundo fallo: pasa al siguiente cierre y reinicia la oportunidad.
      setCurrentFailCount(0);
      if (currentCheckoutIndex === 4) {
        setResultModal({
          visible: true,
          type: currentLevel === 3 ? 'training-complete' : 'level-complete',
        });
      } else {
        setCurrentCheckoutIndex(prev => prev + 1);
      }
    }
  };

  const handleModalContinue = () => {
    setResultModal({ ...resultModal, visible: false });
    
    if (resultModal.type === 'training-complete') {
      // Reiniciar entrenamiento
      resetTraining();
    } else {
      // Siguiente nivel
      const nextLevel = currentLevel + 1;
      setCurrentLevel(nextLevel);
      setCurrentCheckoutIndex(0);
      setCurrentFailCount(0);
      setCheckouts(generateLevelCheckouts(nextLevel));
    }
  };

  const handleModalExit = () => {
    setResultModal({ ...resultModal, visible: false });
    onBack && onBack();
  };

  const resetTraining = () => {
    setCurrentLevel(1);
    setCurrentCheckoutIndex(0);
    setCurrentFailCount(0);
    setRetries([0, 0, 0]);
    setCheckouts(generateLevelCheckouts(1));
  };

  const getLevelTitle = () => {
    if (currentLevel === 1) return 'Nivel 1';
    if (currentLevel === 2) return 'Nivel 2';
    return 'Nivel 3';
  };

  const getLevelDescription = () => {
    if (currentLevel === 1) return 'Posibilidad de cierre con 1 dardo';
    if (currentLevel === 2) return 'Posibilidad de cierre con 2 dardos';
    return 'Posibilidad de cierre con 2 o 3 dardos';
  };

  if (!currentCheckout) {
    return <div className="checkout-training loading">Cargando...</div>;
  }

  // Obtener alternativas de cierre
  const checkoutOptions = getCheckoutOptions(currentCheckout.target);

  // ========== RENDER ==========

  return (
    <div className="checkout-training">
      {/* Logo + Salir arriba del todo (solo móvil) */}
      <div className="ct-logo-top">
        <button className="back-button ct-back-mobile" onClick={() => setShowExitConfirm(true)}>
          ← Salir
        </button>
        <img src="/Logo26.png" alt="501 Dart League" className="ct-app-logo" />
      </div>

      {/* Header */}
      <div className="training-header">
        <button className="back-button" onClick={() => setShowExitConfirm(true)}>
          ← Salir
        </button>
        <img src="/Logo26.png" alt="501 Dart League" className="ct-header-logo" />
        <div className="ct-challenge-info">
          <span className="ct-challenge-title">Entrenamiento de Cierres</span>
          <span className="ct-challenge-desc">{getLevelDescription()}</span>
        </div>
      </div>

      {/* Progreso - misma posición que Jesus Salate */}
      <div className="ct-progress">
        <h2 className="ct-level-title">{getLevelTitle()}</h2>
        <span className="ct-progress-text">Cierre {currentCheckoutIndex + 1}/5</span>
        <div className="ct-progress-dots">
          {[0, 1, 2, 3, 4].map(i => (
            <div 
              key={i} 
              className={`ct-progress-dot ${i < currentCheckoutIndex ? 'completed' : ''} ${i === currentCheckoutIndex ? 'current' : ''}`}
            />
          ))}
        </div>
      </div>

      {/* Game body - misma estructura 3 columnas */}
      <div className="ct-game-body">
        {/* Panel izquierdo - Niveles */}
        <div className="ct-levels-panel">
          <h4>Niveles</h4>
          {[1, 2, 3].map(level => (
            <div 
              key={level}
              className={`ct-level-pill ${level < currentLevel ? 'completed' : ''} ${level === currentLevel ? 'active' : ''}`}
            >
              <span className="ct-level-num">N{level}</span>
              <span className="ct-level-status">
                {level < currentLevel ? '✓' : level === currentLevel ? '●' : '○'}
              </span>
              {retries[level - 1] > 0 && (
                <span className="ct-level-retries">{retries[level - 1]}↻</span>
              )}
            </div>
          ))}
        </div>

        {/* Panel central: Target - misma posición que score */}
        <div className="ct-score-panel">
          <div className="ct-target-number">{currentCheckout.target}</div>
          
          {/* Checkouts */}
          <div className="ct-checkouts">
            {checkoutOptions.slice(0, 3).map((checkout, index) => {
              const formatted = formatCheckout(checkout);
              if (!formatted) return null;
              return (
                <span 
                  key={`checkout-${index}`}
                  className={`ct-checkout ${index === 0 ? 'primary' : 'secondary'}`}
                >
                  {formatted}
                </span>
              );
            })}
          </div>

          {/* Reintentos */}
          <p className={`ct-retries ${retries[currentLevel - 1] > 0 ? 'has-retries' : ''}`}>
            Reintentos: {retries[currentLevel - 1]}
          </p>
        </div>

        {/* Panel derecho - Botones */}
        <div className="ct-buttons-panel">
          <button className="ct-action-btn success" onClick={handleSuccess}>
            <span className="ct-btn-icon">✅</span>
            <span className="ct-btn-text">SÍ, HE CERRADO</span>
            <span className="ct-btn-sub">He marcado {currentCheckout.target} pts</span>
            <span className="ct-kbd-hint">ENTER / S</span>
          </button>
          
          <button className="ct-action-btn fail" onClick={handleFail}>
            <span className="ct-btn-icon">❌</span>
            <span className="ct-btn-text">NO HE CERRADO</span>
            <span className="ct-btn-sub">{currentFailCount === 0 ? 'Tienes otra oportunidad' : 'Siguiente cierre'}</span>
            <span className="ct-kbd-hint">N</span>
          </button>
        </div>
      </div>

      {/* Modal de resultados */}
      <ResultModal
        visible={resultModal.visible}
        type={resultModal.type}
        currentLevel={currentLevel}
        levelRetries={retries[currentLevel - 1]}
        allRetries={retries}
        onContinue={handleModalContinue}
        onExit={handleModalExit}
      />
      
      {/* Modal de confirmación para salir */}
      {showExitConfirm && (
        <div className="modal-overlay">
          <div className="modal-content confirm-modal">
            <h2>¿Salir del entrenamiento?</h2>
            <p>Perderás todo el progreso actual. ¿Estás seguro?</p>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setShowExitConfirm(false)}>
                Cancelar
              </button>
              <button className="btn-danger" onClick={() => {
                setShowExitConfirm(false);
                onBack && onBack();
              }}>
                Salir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patrocinadores */}
      <CTSponsorsStrip />
    </div>
  );
};

const CT_SPONSORS = [
  { href: 'https://www.instagram.com/lokkyokopas2/', img: Lokyo, name: 'Lokkyo Kopas' },
  { href: 'https://www.instagram.com/kingdartleague/', img: KingDart, name: 'Kings Dart League' },
  { href: 'https://www.instagram.com/triple_17_dartshop/', img: triple17yeyo, name: 'Triple 17', logoClassName: 'ct-sponsor-item-logo--triple17' },
  { href: 'https://www.instagram.com/club.dardos.pizarra/', img: patrocinio1, name: 'C.D. Pizarra' },
  { href: 'https://www.instagram.com/salaalexs/', img: patrocinio2, name: "Sala Alex's", logoClassName: 'ct-sponsor-item-logo--large' },
  { href: 'https://www.instagram.com/recreativos_triple17/', img: triple17, name: 'Recreativos Triple 17', logoClassName: 'ct-sponsor-item-logo--triple17' },
  { href: 'https://www.instagram.com/creadoaldetalle/', img: Cad, name: 'CREADO AL DETALLE', logoClassName: 'ct-sponsor-item-logo--large' },
  { href: 'https://www.instagram.com/clubdedardos_cocktails/', img: Cocktails, name: 'Cocktails', logoClassName: 'ct-sponsor-item-logo--cocktails' },
  { href: 'https://www.instagram.com/bar_de_copas_rocha/', img: LogoRocha, name: 'Bar de Copas Rocha', logoClassName: 'ct-sponsor-item-logo--rocha' },
  { href: 'https://www.escobarmultitransportes.es/', img: logoPrima, name: 'Escobar Multitransportes', logoClassName: 'ct-sponsor-item-logo--large' },
  { href: 'https://wa.me/34656820537', img: logoDani, name: 'Daniel Berlanga', logoClassName: 'ct-sponsor-item-logo--large' },
];

const getCTSponsorLogoClassName = (sponsor) => [
  'ct-sponsor-item-logo',
  sponsor.logoClassName,
].filter(Boolean).join(' ');

const CTSponsorsStrip = () => (
  <div className="ct-sponsors-strip">
    <CollaboratorsSection titleClassName="ct-sponsors-label" />
    <span className="ct-sponsors-label">Patrocinadores</span>
    <div className="ct-sponsors-scroll">
      {CT_SPONSORS.map((s, i) => (
        <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" className="ct-sponsor-item">
          <img
            src={s.img}
            alt={s.name}
            className={getCTSponsorLogoClassName(s)}
          />
          <span className="ct-sponsor-name">{s.name}</span>
        </a>
      ))}
    </div>
  </div>
);

export default CheckoutTraining;
